/**
 * Projects Map Module
 * Displays a choropleth map showing project counts by country
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import proj4 from 'proj4';
import 'proj4leaflet';

let map = null;
let geojsonLayer = null;
let projectCounts = {};
let selectedCountry = null; // Track currently selected country
let colorBreaks = [0, 1, 5, 10, 20, 50]; // Dynamic color breaks

// Define Natural Earth projection (Robinson-like projection)
// Using ESRI:54030 (Robinson projection) which is similar to Natural Earth
const naturalEarthCRS = new L.Proj.CRS(
    'ESRI:54030',
    '+proj=robin +lon_0=0 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs',
    {
        resolutions: [
            65536, 32768, 16384, 8192, 4096, 2048
        ],
        origin: [0, 0],
        bounds: L.bounds(
            [-17005833.3305252, -8625154.47184994],
            [17005833.3305252, 8625154.47184994]
        )
    }
);

/**
 * Calculate dynamic color breaks based on current project counts
 */
function calculateColorBreaks() {
    const counts = Object.values(projectCounts).filter(c => c > 0);
    if (counts.length === 0) {
        colorBreaks = [0, 1, 5, 10, 20, 50];
        return;
    }

    const maxCount = Math.max(...counts);

    // Use quantiles for better distribution
    counts.sort((a, b) => a - b);

    if (maxCount <= 5) {
        colorBreaks = [0, 1, 2, 3, 4, 5];
    } else if (maxCount <= 20) {
        const q1 = counts[Math.floor(counts.length * 0.2)];
        const q2 = counts[Math.floor(counts.length * 0.4)];
        const q3 = counts[Math.floor(counts.length * 0.6)];
        const q4 = counts[Math.floor(counts.length * 0.8)];
        colorBreaks = [0, 1, Math.ceil(q1), Math.ceil(q2), Math.ceil(q3), Math.ceil(q4)];
    } else {
        const q1 = counts[Math.floor(counts.length * 0.2)];
        const q2 = counts[Math.floor(counts.length * 0.4)];
        const q3 = counts[Math.floor(counts.length * 0.6)];
        const q4 = counts[Math.floor(counts.length * 0.8)];
        colorBreaks = [0, 1, Math.ceil(q1), Math.ceil(q2), Math.ceil(q3), Math.ceil(q4)];
    }
}

/**
 * Resolve a feature's ISO-2 country code.
 * Natural Earth tags France, Norway and Kosovo with ISO_A2 = "-99" (a known
 * upstream data bug); the real code lives in ISO_A2_EH. Fall back to it so
 * those countries still match project data. "-99" is a truthy string, so a
 * plain `a || b` is not enough — check for it explicitly.
 */
function featureIso(feature) {
    const p = feature.properties;
    let iso = p.ISO_A2 || p.iso_a2;
    if (!iso || iso === '-99') {
        iso = p.ISO_A2_EH || p.iso_a2_eh;
    }
    return (iso && iso !== '-99') ? iso : null;
}

/**
 * Color scale for choropleth
 */
function getColor(count) {
    if (count === 0) return '#e8e8e8';  // Grey for countries with 0 projects
    if (count > colorBreaks[5]) return '#014636';
    if (count > colorBreaks[4]) return '#02988f';
    if (count > colorBreaks[3]) return '#20b2aa';
    if (count > colorBreaks[2]) return '#5dc9c0';
    if (count > colorBreaks[1]) return '#a0e0db';
    if (count > 0) return '#e0f2f1';
    return '#e8e8e8';
}

/**
 * Style function for GeoJSON features
 */
function style(feature) {
    const iso = featureIso(feature);
    const count = projectCounts[iso] || 0;

    return {
        fillColor: getColor(count),
        weight: 0.5,
        opacity: 1,
        color: '#cccccc',
        fillOpacity: 0.8
    };
}

/**
 * Highlight feature on hover
 */
function highlightFeature(e) {
    const layer = e.target;

    layer.setStyle({
        weight: 2,
        color: '#666',
        fillOpacity: 0.9
    });

    layer.bringToFront();
}

/**
 * Reset highlight
 */
function resetHighlight(e) {
    if (geojsonLayer) {
        geojsonLayer.resetStyle(e.target);
    }
}

/**
 * Handle country click - filter projects by country
 * Clicking the same country again clears the filter
 */
function onCountryClick(e) {
    const feature = e.target.feature;
    const countryName = feature.properties.NAME || feature.properties.name;
    const iso = featureIso(feature);
    const count = projectCounts[iso] || 0;

    // Only filter if country has projects
    if (count > 0) {
        // Toggle filter: if clicking same country, clear filter
        if (selectedCountry === countryName) {
            clearCountryFilter();
        } else {
            // Use the globally exposed filter function
            if (typeof window.filterProjectsByCountry === 'function') {
                window.filterProjectsByCountry(countryName);
                selectedCountry = countryName;
            } else {
                console.warn('filterProjectsByCountry function not found');
            }
        }
    }
}

/**
 * Clear country filter and show all projects
 */
function clearCountryFilter() {
    if (typeof window.clearProjectsCountryFilter === 'function') {
        window.clearProjectsCountryFilter();
        selectedCountry = null;
    }
}

/**
 * Bind events to each feature
 */
function onEachFeature(feature, layer) {
    const iso = featureIso(feature);
    const countryName = feature.properties.NAME || feature.properties.name;
    const count = projectCounts[iso] || 0;

    // Bind tooltip for all countries
    const tooltipContent = count > 0
        ? `<strong>${countryName}</strong><br>${count} project${count > 1 ? 's' : ''}`
        : `<strong>${countryName}</strong><br>No projects`;

    layer.bindTooltip(tooltipContent, {
        sticky: true,
        direction: 'auto'
    });

    // Bind hover events
    layer.on({
        mouseover: highlightFeature,
        mouseout: resetHighlight,
        click: onCountryClick
    });
}

/**
 * Initialize the map
 * Returns an object with map instance and a promise that resolves when data is loaded
 */
export function initializeProjectsMap(containerId = 'projects-map') {
    const container = document.getElementById(containerId);
    if (!container) {
        console.warn('Map container not found:', containerId);
        return null;
    }

    // Initialize map with Natural Earth projection and white background
    map = L.map(containerId, {
        crs: naturalEarthCRS,
        center: [10, 20],  // Adjusted to center better (slightly south, shifted west)
        zoom: 1,
        minZoom: 0,
        maxZoom: 4,
        zoomControl: true,
        attributionControl: false,
        worldCopyJump: false
    });

    // Add click handler for map background (clicking outside countries)
    map.on('click', function(e) {
        // If click was not on a feature, clear the filter
        if (!e.originalEvent.target.classList.contains('leaflet-interactive')) {
            clearCountryFilter();
        }
    });

    // Load world boundaries and project data (returns promise)
    const ready = loadMapData();

    return { map, ready };
}

/**
 * Load GeoJSON boundaries (country polygons)
 * Returns a promise that resolves when map data is fully loaded
 */
async function loadMapData() {
    try {
        // Load world boundaries GeoJSON from local static file
        const geoResponse = await fetch('/static/data/countries.geojson');
        const geoData = await geoResponse.json();

        // Add GeoJSON layer to map
        geojsonLayer = L.geoJSON(geoData, {
            style: style,
            onEachFeature: onEachFeature
        }).addTo(map);

        // Add legend (commented out for now)
        // addLegend();

        return true; // Success

    } catch (error) {
        console.error('Error loading map data:', error);
        // Show error message on map container
        if (map) {
            const mapContainer = document.getElementById('projects-map');
            if (mapContainer) {
                mapContainer.innerHTML = `
                    <div style="padding: 2rem; text-align: center; color: #dc3545;">
                        <i class="fas fa-exclamation-triangle fa-2x mb-2"></i>
                        <div>Failed to load map data</div>
                        <small>${error.message}</small>
                    </div>
                `;
            }
        }
        throw error; // Re-throw so caller knows it failed
    }
}

// /**
//  * Add legend to map (commented out for now)
//  */
// function addLegend() {
//     const legend = L.control({ position: 'bottomright' });
//
//     legend.onAdd = function () {
//         const div = L.DomUtil.create('div', 'map-legend');
//         const grades = [0, 1, 5, 10, 20, 50];
//         const labels = [];
//
//         div.innerHTML = '<h4>Projects</h4>';
//
//         // Generate a label with a colored square for each interval
//         for (let i = 0; i < grades.length; i++) {
//             const from = grades[i];
//             const to = grades[i + 1];
//
//             labels.push(
//                 '<i style="background:' + getColor(from + 1) + '"></i> ' +
//                 from + (to ? '&ndash;' + to : '+')
//             );
//         }
//
//         div.innerHTML += labels.join('<br>');
//         return div;
//     };
//
//     legend.addTo(map);
// }

/**
 * Update map with filtered table data
 * @param {Array} filteredRows - Array of row data objects from the filtered table
 */
export function updateMapFromTable(filteredRows) {
    if (!map || !geojsonLayer) return;

    // Count projects by ISO code from filtered table data
    const isoCounts = {};

    filteredRows.forEach(row => {
        if (row.country_isos && row.country_isos.trim()) {
            // Split comma-separated ISO codes
            const isos = row.country_isos.split(',').map(iso => iso.trim().toUpperCase());
            isos.forEach(iso => {
                if (iso) {
                    isoCounts[iso] = (isoCounts[iso] || 0) + 1;
                }
            });
        }
    });

    // Update projectCounts
    projectCounts = isoCounts;

    // Recalculate color breaks based on new data distribution
    calculateColorBreaks();

    // Re-style the GeoJSON layer with new counts
    if (geojsonLayer) {
        geojsonLayer.setStyle(style);

        // Update tooltips with new counts
        geojsonLayer.eachLayer(layer => {
            const feature = layer.feature;
            if (feature) {
                const iso = featureIso(feature);
                const countryName = feature.properties.NAME || feature.properties.name;
                const count = projectCounts[iso] || 0;

                const tooltipContent = count > 0
                    ? `<strong>${countryName}</strong><br>${count} project${count > 1 ? 's' : ''}`
                    : `<strong>${countryName}</strong><br>No projects`;

                layer.setTooltipContent(tooltipContent);
            }
        });
    }
}

/**
 * Destroy map instance
 */
export function destroyMap() {
    if (map) {
        map.remove();
        map = null;
        geojsonLayer = null;
        projectCounts = {};
    }
}
